import type { BestDecisionsResult } from "@beyourbest/decision-engine";
import { type Player, type PlayerAction, type ScenarioClip } from "@Containers/game/builder/models/builder-data-types";
import ScenarioActionType from "@Enums/ScenarioActionType";
import { clamp } from "lodash-es";

export const AUTO_DECISION_SCORE_SOURCE = "auto-decision-timeline";
export const DEFAULT_SCORE_ACTION_TOLERANCE = 0.05;
export const DEFAULT_SCORE_ACTION_LEAD_IN = 0.35;
export const DEFAULT_SCORE_ACTION_TRAIL_OUT = 2;
export const DEFAULT_SCORE_ACTION_MIN_DURATION = 1.5;
export const MIN_GOOD_DECISION_SCORE = 70;

export const getAutoPlayoutTime = (score: number) => {
  return score < MIN_GOOD_DECISION_SCORE ? 0.5 : 3;
};

export const toInternalPlayerId = (value: number | string | undefined) => {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string") {
    const parsed = Number.parseInt(value, 10);
    return Number.isFinite(parsed) ? parsed : null;
  }

  return null;
};

export const hasScoreActionForWindow = (player: Player, startTime: number, endTime: number, tolerance = DEFAULT_SCORE_ACTION_TOLERANCE) => {
  return player.actions.some(
    action =>
      action.type === ScenarioActionType.Score &&
      Math.abs(action.startTime - startTime) <= tolerance &&
      Math.abs(action.endTime - endTime) <= tolerance
  );
};

export const toRoundedScore = (value: number) => {
  return clamp(Math.round(value), 0, 100);
};

export type DecisionTimelineSample = {
  evalTime: number;
  displayScore: number;
  passScore: number;
  xP: number;
  xPT: number;
  xG: number;
};

export type DecisionSegment = {
  startTime: number;
  endTime: number;
  score: number;
};

export type DecisionAssignmentItem = {
  targetPlayerId?: number | string;
  passType?: string;
  evalTime?: number;
  displayScore?: number;
  samples?: DecisionTimelineSample[];
};

export const clonePlayersWithActions = (players: ScenarioClip["scenario"]["players"]) => {
  const clonedPlayers = new Map<number, Player>();

  players.forEach((player: Player, playerId: number) => {
    clonedPlayers.set(playerId, {
      ...player,
      actions: player.actions.map((action: PlayerAction) => ({ ...action })),
    });
  });

  return clonedPlayers;
};

export const clearExistingScoreActions = (players: Map<number, Player>) => {
  let removedCount = 0;

  players.forEach(player => {
    const beforeCount = player.actions.length;
    player.actions = player.actions.filter(action => action.type !== ScenarioActionType.Score);
    removedCount += beforeCount - player.actions.length;
  });

  return { removedCount };
};

export const resolvePlayerMapKey = (players: Map<number, Player>, internalPlayerId: number | null) => {
  if (internalPlayerId === null) return null;
  if (players.has(internalPlayerId)) return internalPlayerId;

  for (const [playerKey, player] of players.entries()) {
    if (player.id === internalPlayerId || player.jerseyNumber === internalPlayerId) {
      return playerKey;
    }
  }

  return null;
};

export const removeScoreActionsInWindow = (player: Player, startTime: number, endTime: number) => {
  const trimmedActions: PlayerAction[] = [];

  player.actions.forEach(action => {
    if (action.type !== ScenarioActionType.Score) {
      trimmedActions.push(action);
      return;
    }

    const overlaps = action.startTime < endTime - DEFAULT_SCORE_ACTION_TOLERANCE && action.endTime > startTime + DEFAULT_SCORE_ACTION_TOLERANCE;
    if (!overlaps) {
      trimmedActions.push(action);
      return;
    }

    if (action.startTime < startTime - DEFAULT_SCORE_ACTION_TOLERANCE) {
      trimmedActions.push({
        ...action,
        endTime: startTime,
      });
    }

    if (action.endTime > endTime + DEFAULT_SCORE_ACTION_TOLERANCE) {
      trimmedActions.push({
        ...action,
        startTime: endTime,
      });
    }
  });

  player.actions = trimmedActions;
};

export const appendScoreAction = (params: { player: Player; startTime: number; endTime: number; score: number; isShotDecision: boolean }) => {
  const { player, startTime, endTime, score, isShotDecision } = params;

  if (hasScoreActionForWindow(player, startTime, endTime, DEFAULT_SCORE_ACTION_TOLERANCE)) {
    return false;
  }

  const scoreAction: PlayerAction = {
    type: ScenarioActionType.Score,
    startTime,
    endTime,
    targetPlayer: -1,
    targetPosition: { x: 0, y: 0 },
    data: { source: AUTO_DECISION_SCORE_SOURCE },
    index: score,
    height: 0,
    direction: 0,
    time: isShotDecision ? 1 : getAutoPlayoutTime(score),
    isGenerated: true,
  };

  player.actions.push(scoreAction);
  return true;
};

export const buildFallbackSamples = (decisionItem: DecisionAssignmentItem, timelineEvalTime: number): DecisionTimelineSample[] => {
  if (decisionItem.samples && decisionItem.samples.length > 0) {
    return decisionItem.samples;
  }

  return [
    {
      evalTime: timelineEvalTime,
      displayScore: decisionItem.displayScore ?? 0,
      passScore: 0,
      xP: 0,
      xPT: 0,
      xG: 0,
    },
  ];
};

export const buildTimelineSegments = (samples: DecisionTimelineSample[] | undefined, windowStart: number, windowEnd: number): DecisionSegment[] => {
  if (!samples || samples.length === 0) return [];

  const ordered = samples
    .filter(sample => Number.isFinite(sample.evalTime))
    .map(sample => ({
      evalTime: clamp(sample.evalTime, windowStart, windowEnd),
      score: toRoundedScore(sample.displayScore ?? 0),
      passScore: sample.passScore ?? Number.NEGATIVE_INFINITY,
    }))
    .sort((a, b) => a.evalTime - b.evalTime);

  if (ordered.length === 0) return [];

  const deduped: Array<{ evalTime: number; score: number; passScore: number }> = [];
  ordered.forEach(sample => {
    const last = deduped[deduped.length - 1];
    if (last && Math.abs(last.evalTime - sample.evalTime) <= DEFAULT_SCORE_ACTION_TOLERANCE) {
      if (sample.passScore >= last.passScore) {
        deduped[deduped.length - 1] = sample;
      }
      return;
    }

    deduped.push(sample);
  });

  if (deduped.length === 0) return [];

  if (deduped.length === 1) {
    const only = deduped[0];
    if (windowEnd > windowStart + DEFAULT_SCORE_ACTION_TOLERANCE) {
      return [{ startTime: windowStart, endTime: windowEnd, score: only.score }];
    }

    return [];
  }

  const segments: DecisionSegment[] = [];

  for (let i = 0; i < deduped.length - 1; i++) {
    const sample = deduped[i];
    const nextSample = deduped[i + 1];
    const startTime = i === 0 ? windowStart : sample.evalTime;
    const endTime = nextSample.evalTime;

    if (endTime <= startTime + DEFAULT_SCORE_ACTION_TOLERANCE) continue;

    const lastSegment = segments[segments.length - 1];
    if (lastSegment && lastSegment.score === sample.score && Math.abs(lastSegment.endTime - startTime) <= DEFAULT_SCORE_ACTION_TOLERANCE) {
      lastSegment.endTime = endTime;
    } else {
      segments.push({ startTime, endTime, score: sample.score });
    }
  }

  const lastSample = deduped[deduped.length - 1];
  const tailEndTime = clamp(windowEnd, lastSample.evalTime, windowEnd);
  if (tailEndTime > lastSample.evalTime + DEFAULT_SCORE_ACTION_TOLERANCE) {
    const lastSegment = segments[segments.length - 1];
    if (
      lastSegment &&
      lastSegment.score === lastSample.score &&
      Math.abs(lastSegment.endTime - lastSample.evalTime) <= DEFAULT_SCORE_ACTION_TOLERANCE
    ) {
      lastSegment.endTime = tailEndTime;
    } else {
      segments.push({
        startTime: lastSample.evalTime,
        endTime: tailEndTime,
        score: lastSample.score,
      });
    }
  }

  return segments;
};

export const getDecisionWindow = (params: {
  moment: BestDecisionsResult;
  playbackResolution: number;
  scenarioEndTime: number;
  decisionItems: DecisionAssignmentItem[];
}) => {
  const { moment, playbackResolution, scenarioEndTime, decisionItems } = params;

  const allEvalTimes = decisionItems.flatMap(item => {
    const evalTimes: number[] = [];
    if (Number.isFinite(item.evalTime)) evalTimes.push(item.evalTime as number);
    (item.samples ?? []).forEach(sample => {
      if (Number.isFinite(sample.evalTime)) evalTimes.push(sample.evalTime);
    });
    return evalTimes;
  });

  const minEvalTime = allEvalTimes.length ? Math.min(...allEvalTimes) : 0;
  const maxEvalTime = allEvalTimes.length ? Math.max(...allEvalTimes) : minEvalTime;
  const minWindowStep = Math.max(playbackResolution || 0.05, DEFAULT_SCORE_ACTION_TOLERANCE * 2, DEFAULT_SCORE_ACTION_MIN_DURATION);
  const fallbackWindowStart = Number.isFinite(minEvalTime) ? minEvalTime : 0;
  const momentStart = Number.isFinite(moment.startTime) ? moment.startTime : fallbackWindowStart;
  const windowStart = clamp(momentStart - DEFAULT_SCORE_ACTION_LEAD_IN, 0, scenarioEndTime);
  const fallbackWindowEnd = Number.isFinite(maxEvalTime) ? maxEvalTime : momentStart + minWindowStep;
  const proposedWindowEnd = Number.isFinite(moment.endTime) ? moment.endTime : fallbackWindowEnd;
  const extendedWindowEnd = proposedWindowEnd + DEFAULT_SCORE_ACTION_TRAIL_OUT;
  const windowEnd = clamp(Math.max(extendedWindowEnd, windowStart + DEFAULT_SCORE_ACTION_MIN_DURATION), windowStart, scenarioEndTime);

  return { momentStart, windowStart, windowEnd };
};

export const getDecisionItems = (moment: BestDecisionsResult): DecisionAssignmentItem[] => {
  const timeline = moment.decisions?.timeline ?? [];

  return timeline.map(item => ({
    targetPlayerId: item.targetPlayerId,
    passType: item.passType,
    evalTime: item.evalTime,
    displayScore: (item as { displayScore?: number }).displayScore,
    samples: item.samples as DecisionTimelineSample[] | undefined,
  }));
};

export const getDefaultZeroScoreSegments = (windowStart: number, windowEnd: number): DecisionSegment[] => {
  if (windowEnd <= windowStart + DEFAULT_SCORE_ACTION_TOLERANCE) {
    return [];
  }

  return [{ startTime: windowStart, endTime: windowEnd, score: 0 }];
};
